import { consumeCodeVerifier, createCodeChallenge, createCodeVerifier, storeCodeVerifier } from '@/auth/pkce'

export type TEntraTokenResponse = {
  access_token: string
  id_token?: string
  refresh_token?: string
  token_type?: string
  expires_in?: number
  scope?: string
}

/**
 * Microsoft Entra ID (OAuth 2.0 + PKCE). No MSAL.
 * authorize → code in redirect URL → token endpoint → clear code from URL.
 *
 * Request the custom API scope first. Do not request Microsoft Graph scopes
 * (e.g. User.Read) in the same authorize call — Entra then mints a Graph
 * access token (header nonce) that this API cannot validate.
 */
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID ?? ''

export const entraConfig = {
  clientId,
  authority: import.meta.env.VITE_ENTRA_AUTHORITY ?? '',
  redirectUri: import.meta.env.VITE_ENTRA_REDIRECT_URI ?? window.location.origin,
  scopes: [`api://${clientId}/access_as_user`, 'openid', 'profile', 'email'] as const,
}

const getTokenEndpoint = () => `${entraConfig.authority.replace(/\/$/, '')}/oauth2/v2.0/token`

export const getEntraAuthorizeUrl = async (params?: { state?: string; redirectUri?: string }): Promise<string> => {
  const redirectUri = params?.redirectUri ?? entraConfig.redirectUri
  const verifier = createCodeVerifier()
  storeCodeVerifier(verifier)
  const challenge = await createCodeChallenge(verifier)

  const url = new URL(`${entraConfig.authority.replace(/\/$/, '')}/oauth2/v2.0/authorize`)
  url.searchParams.set('client_id', entraConfig.clientId)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('redirect_uri', redirectUri)
  url.searchParams.set('response_mode', 'query')
  url.searchParams.set('scope', entraConfig.scopes.join(' '))
  // Force account picker so we don't silently reuse an old Graph-only session.
  url.searchParams.set('prompt', 'select_account')
  url.searchParams.set('code_challenge', challenge)
  url.searchParams.set('code_challenge_method', 'S256')
  if (params?.state) {
    url.searchParams.set('state', params.state)
  }
  return url.toString()
}

export const exchangeEntraCode = async (code: string): Promise<TEntraTokenResponse> => {
  const body = new URLSearchParams({
    client_id: entraConfig.clientId,
    grant_type: 'authorization_code',
    code,
    redirect_uri: entraConfig.redirectUri,
    scope: entraConfig.scopes.join(' '),
  })

  const verifier = consumeCodeVerifier()
  if (verifier) {
    body.set('code_verifier', verifier)
  }

  const response = await fetch(getTokenEndpoint(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })

  const payload = (await response.json()) as TEntraTokenResponse & { error?: string; error_description?: string }

  if (!response.ok) {
    throw new Error(payload.error_description || payload.error || 'Entra token exchange failed')
  }

  return payload
}

export const clearEntraParamsFromUrl = (): void => {
  const url = new URL(window.location.href)
  ;['code', 'state', 'session_state', 'error', 'error_description'].forEach((key) => url.searchParams.delete(key))
  const next = `${url.pathname}${url.search}${url.hash}`
  window.history.replaceState({}, document.title, next || '/')
}

export const parseIdTokenClaims = (token: string): Record<string, unknown> => {
  const payload = token.split('.')[1]
  if (!payload) {
    return {}
  }

  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=')
  try {
    return JSON.parse(atob(padded)) as Record<string, unknown>
  } catch {
    return {}
  }
}

const getStringClaim = (claims: Record<string, unknown>, key: string): string | undefined => {
  const value = claims[key]
  return typeof value === 'string' && value.trim() ? value : undefined
}

/** Read name/email from Entra JWTs (access_token and optional id_token). */
export const getEntraProfileFromTokens = (tokens: { id_token?: string; access_token: string }) => {
  const accessClaims = parseIdTokenClaims(tokens.access_token)
  const idClaims = tokens.id_token ? parseIdTokenClaims(tokens.id_token) : {}

  const email =
    getStringClaim(idClaims, 'email') ||
    getStringClaim(accessClaims, 'email') ||
    getStringClaim(idClaims, 'preferred_username') ||
    getStringClaim(accessClaims, 'preferred_username') ||
    getStringClaim(idClaims, 'upn') ||
    getStringClaim(accessClaims, 'upn') ||
    getStringClaim(idClaims, 'unique_name') ||
    getStringClaim(accessClaims, 'unique_name')

  const userName = getStringClaim(idClaims, 'name') || getStringClaim(accessClaims, 'name') || email || 'Entra User'

  return { userName, email }
}
